import { DataTable } from '@/components/ui/data-table'
import { CellContext, ColumnDef } from '@tanstack/react-table'
import { genericSorting } from 'app/frontend/base/config/column-definitions/util'
import { useTeam } from 'app/frontend/base/config/context/team'
import { useMemo, useState } from 'react'
import { Trans } from 'react-i18next'
import { Link } from 'react-router-dom'
import AddMemberModal from '../../TeamRoster/components/AddMemberModal'
import CharacterRoleIcon from 'app/frontend/base/components/CharacterRoleIcon'
import AuditCategory from 'app/frontend/types/custom/AuditCategory'

import { useCollectionsColumns } from '../column-definitions/collections'
import { useDungeonsOverallColumns } from '../column-definitions/dungeons_overall'
import { useDungeonsWeeklyColumns } from '../column-definitions/dungeons_weekly'
import { useEnchantmentsColumns } from '../column-definitions/enchantments'
import { useGearOverviewColumns } from '../column-definitions/gear_overview'
import { useGearUpgradesColumns } from '../column-definitions/gear_upgrades'
import { usePvpColumns } from '../column-definitions/pvp'
import {
  useRaidsOverallColumns,
  useRaidsWeeklyColumns,
} from '../column-definitions/raids'
import { useReputationsColumns } from '../column-definitions/reputations'
import { useSummaryColumns } from '../column-definitions/summary'
import { useVaultColumns } from '../column-definitions/vault'
import { EnrichedTeamMembership } from '../types'
import { useAudit } from '../context'
import { CaretLeftIcon, CaretRightIcon } from '@radix-ui/react-icons'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { ChartLine, Medal, Table } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Skeleton } from '@/components/ui/skeleton'
import AuditGraph from './AuditGraph'

type Props = {
  category: AuditCategory
  embedded?: boolean
}

const AuditTable = ({ category, embedded }: Props) => {
  const { team } = useTeam()
  const [addingMembers, setAddingMembers] = useState(false)
  const {
    visibleMembers,
    weeksAgo,
    setWeeksAgo,
    showingRanks,
    setShowingRanks,
    activeView,
    setActiveView,
  } = useAudit()

  const columnHooks = {
    collections: useCollectionsColumns,
    dungeons_overall: useDungeonsOverallColumns,
    dungeons_weekly: useDungeonsWeeklyColumns,
    enchantments: useEnchantmentsColumns,
    gear_overview: useGearOverviewColumns,
    gear_upgrades: useGearUpgradesColumns,
    pvp: usePvpColumns,
    raids_overall: useRaidsOverallColumns,
    raids_weekly: useRaidsWeeklyColumns,
    reputations: useReputationsColumns,
    summary: useSummaryColumns,
    vault: useVaultColumns,
  }

  const {
    columns: categoryColumns,
    filters,
    initialSortBy,
    graphData,
    hasHistory,
  } = columnHooks[category]()

  const isLoading = visibleMembers.length > 0 && !visibleMembers[0].data

  const columns: ColumnDef<EnrichedTeamMembership>[] = useMemo(
    () => [
      {
        accessorKey: 'characterReference.name',
        id: 'characterReference.name',
        size: 175,
        ...genericSorting<EnrichedTeamMembership>('text', 'Name'),
        cell: (info: CellContext<EnrichedTeamMembership, unknown>) => {
          return (
            <div className='flex'>
              <Link
                to={info.row.original.characterReference.path}
                className='inline-flex gap-2 items-center'
              >
                <CharacterRoleIcon
                  role={info.row.original.role}
                  className='h-6 w-6'
                />

                {info.row.original.characterReference.name}
              </Link>
            </div>
          )
        },
      },
      ...categoryColumns,
    ],
    [category, categoryColumns],
  )

  return (
    <>
      {!embedded && (
        <div className='mt-6 flex gap-2 justify-between items-end'>
          <div className='flex gap-2 flex-wrap'>{filters}</div>

          <div className='flex gap-2 mt-5 flex-wrap'>
            {hasHistory && activeView === 'list' ? (
              <div className='flex items-center justify-between gap-1 w-[11.5rem]'>
                <Button
                  variant='outline'
                  size='sm'
                  onClick={() => setWeeksAgo(weeksAgo + 1)}
                >
                  <CaretLeftIcon />
                </Button>
                {weeksAgo === 0 ? (
                  <span>This week</span>
                ) : (
                  <div className='flex items-center gap-2 grow'>
                    <Input
                      type='number'
                      className='w-12 h-9 text-center'
                      value={weeksAgo}
                      onChange={(e) => setWeeksAgo(Number(e.target.value))}
                    />
                    <div className='flex flex-col text-xs items-center grow'>
                      <span>{weeksAgo === 1 ? 'week' : 'weeks'}</span>
                      <span>ago</span>
                    </div>
                  </div>
                )}
                <Button
                  disabled={weeksAgo === 0}
                  variant='outline'
                  size='sm'
                  onClick={() => setWeeksAgo(weeksAgo - 1)}
                >
                  <CaretRightIcon />
                </Button>
              </div>
            ) : null}

            {graphData ? (
              <Tabs
                value={activeView}
                onValueChange={(value) =>
                  setActiveView(value as 'graph' | 'list')
                }
              >
                <TabsList className='min-h-8'>
                  <TabsTrigger value='graph'>
                    <ChartLine className='w-4 h-4' />
                  </TabsTrigger>
                  <TabsTrigger value='list'>
                    <Table className='w-4 h-4' />
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            ) : null}

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant='outline'
                  size='sm'
                  onClick={() => setShowingRanks(!showingRanks)}
                >
                  <Medal
                    className={cn(
                      'w-4 h-4',
                      showingRanks ? 'text-primary' : 'opacity-30',
                    )}
                  />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {showingRanks
                  ? 'Showing rank columns'
                  : 'Not showing rank columns'}
              </TooltipContent>
            </Tooltip>
          </div>
        </div>
      )}

      {isLoading ? (
        <Skeleton className='mt-2 h-[40rem] w-full' />
      ) : activeView === 'list' || !graphData ? (
        <DataTable
          columns={columns}
          key={category}
          data={visibleMembers}
          headerClassName={(i) => (i === 0 ? 'text-left' : 'text-center')}
          rowClassName={() => 'text-center'}
          className={cn('bg-background', embedded ? '' : 'mt-2')}
          includeHeader={true}
          sortingState={
            initialSortBy ?? [{ id: 'characterReference.name', desc: false }]
          }
          emptyMessage={() => (
            <Trans
              i18nKey={
                team.members?.length
                  ? 'dashboard.team.table-all-invisible'
                  : 'dashboard.team.table-no-members'
              }
              components={{
                link: <Link to={`${team.path}/settings#roster`} />,
                modal: (
                  <a
                    className='text-primary cursor-pointer'
                    onClick={() => setAddingMembers(true)}
                  />
                ),
              }}
            />
          )}
          visibilityState={{
            rank: showingRanks,
          }}
          stickyFirstColumn
        />
      ) : (
        <AuditGraph config={graphData.config} values={graphData.values} />
      )}

      {!embedded && addingMembers && (
        <AddMemberModal open={addingMembers} setOpen={setAddingMembers} />
      )}
    </>
  )
}

export default AuditTable
